import { RedirectFunction } from "@angular/router";
import { inject } from "@angular/core";
import { IAPI_APP_ROUTER_SERVICE } from "../constants/iapi-app-tokens";
import { AuthenticationService } from "../services/authentication.service";
import { INotification } from "../../notification/interfaces/notification";
import { NotificationType } from "../../notification/enums/notification-type.enum";

/**
 * LogoutRedirect
 *
 * Handles the logout route redirect. Builds a success or error notification based
 * on whether the router has an error message set (e.g., from a session-expiry or
 * unauthorised-access event), calls `handleLogout` on the `AuthenticationService`,
 * and redirects the user to the login page.
 *
 * ## Redirect Logic:
 *
 * 1. Sets the post-login path from the logout context via `setPostLoginPathFromLogout`.
 * 2. If `appRouter.errorMessage` is set, overrides the notification to an error with
 *    that message; otherwise uses a success "You have been logged out" notification.
 * 3. Calls `authService.handleLogout(notification)` to clear auth state and display
 *    the notification.
 * 4. Returns `appRouter.getLoginUrl()` as the redirect target.
 *
 * @returns The login URL string to redirect to after logout.
 *
 * @example
 * ```typescript
 * // In iapi-app.routes.ts:
 * {
 *   path: "logout",
 *   data: { routeId: AuthenticationRouteId.Logout },
 *   redirectTo: logoutRedirect
 * }
 * ```
 */
export const logoutRedirect: RedirectFunction = () => {
    const appRouter = inject(IAPI_APP_ROUTER_SERVICE);
    const authService = inject(AuthenticationService);
    let notification: INotification = {
        type: NotificationType.Success,
        message: "You have been logged out"
    };
    // This function will set a post login path if the navigation is
    // the result of an error
    appRouter.setPostLoginPathFromLogout();
    if (appRouter.errorMessage) {
        notification = {
            type: NotificationType.Error,
            message: appRouter.errorMessage
        };
    }
    // Log the user out
    authService.handleLogout(notification);
    // Redirect to the login page
    return appRouter.getLoginUrl();
};
